/**
 * N3 · Competência 2 (Álgebra e funções) · task-model H1.8.K02
 *
 *   "Ajustar os coeficientes de uma função afim até o gráfico passar por dois pontos."
 *
 * Dois pontos marcados no plano e a reta y = ax + b, com um controle para a e outro
 * para b. O aluno move os dois até a reta passar pelos pontos. Um controle só não
 * resolve: acertar a inclinação sem o deslocamento dá uma paralela; acertar o corte no
 * eixo y sem a inclinação faz a reta girar em torno do ponto errado. É o jogo entre os
 * dois coeficientes que o item mede — e os desfechos incompletos têm nome.
 *
 * O ajuste é por tentativa, e isso é deliberado: quem entende o papel de cada
 * coeficiente chega em poucos movimentos; quem não entende gira os dois controles ao
 * acaso. A contagem de movimentos vai na evidência, para a calibração separar os dois.
 *
 * Crédito parcial (GPCM): metade pela inclinação, metade pelo corte no eixo y.
 */

import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import { afimScene } from '../../render/chart.ts';
import { scene, type Shape } from '../../render/scene.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import {
  FUN_AJUSTE_PARALELA,
  FUN_AJUSTE_SO_CORTE,
  FUN_AJUSTE_UM_PONTO,
  FUN_SINAL_ANGULAR,
  FUN_TROCA_A_B,
} from '../../schema/errors.ts';
import type { ControlTarget, ItemTemplate } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, tex, txt } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

/** Semi-extensão do quadro e escala de desenho. */
const SPAN = 6;
const UN = 22;

const ANGULARES = [-3, -2, -1, -0.5, 0.5, 2, 3] as const;
const LINEARES = [-4, -3, -2, -1, 1, 2, 3, 4] as const;

/** Os controles: a em meios de −4 a 4, b em inteiros de −6 a 6. */
const A_MIN = -4;
const A_MAX = 4;
const B_MIN = -6;
const B_MAX = 6;
/** Onde os controles começam: y = x. Nenhuma chave tem a = 1 nem b = 0. */
const A0 = 1;
const B0 = 0;

export interface Params {
  readonly a: number;
  readonly b: number;
  readonly x1: number;
  readonly x2: number;
}

/** Abscissas em que a reta passa por um ponto da malha, dentro do quadro. */
function abscissasBoas(a: number, b: number): number[] {
  const xs: number[] = [];
  for (let x = -(SPAN - 1); x <= SPAN - 1; x++) {
    const y = a * x + b;
    if (Number.isInteger(y) && Math.abs(y) <= SPAN - 1) xs.push(x);
  }
  return xs;
}

function drawParams(rng: Rng): Params {
  const { a, b } = sampleWhere(
    rng.fork('reta'),
    (r) => ({ a: r.pick(ANGULARES), b: r.pick(LINEARES) }),
    // b ≠ a: senão "trocou a por b" seria a própria chave.
    (c) => c.b !== c.a && abscissasBoas(c.a, c.b).length >= 2,
    { label: 'reta' },
  );
  const xs = abscissasBoas(a, b);
  const pares: [number, number][] = [];
  for (const x1 of xs) for (const x2 of xs) if (x2 - x1 >= 2) pares.push([x1, x2]);
  const [x1, x2] = rng.fork('pontos').pick(pares);
  return { a, b, x1, x2 };
}

const menos = (v: number): string => (v < 0 ? `−${-v}` : `${v}`);

/** O coeficiente angular como no livro: "2", "−3", "1/2", "−3/2". */
function fracao(v: number): string {
  if (Number.isInteger(v)) return menos(v);
  const n = Math.round(Math.abs(v) * 2);
  return `${v < 0 ? '−' : ''}${n}/2`;
}

const pt = (x: number, y: number): string => `(${menos(x)}, ${menos(y)})`;
const igual = (x: number | undefined, y: number): boolean =>
  x !== undefined && Math.abs(x - y) < 1e-9;

/** "y = 2x − 3", "y = −x/2 + 1" — a lei com os valores dos controles. */
function lei(a: number, b: number): string {
  const ax = a === 0 ? '' : a === 1 ? 'x' : a === -1 ? '−x' : `${fracao(a)}x`;
  if (ax === '') return `y = ${menos(b)}`;
  if (b === 0) return `y = ${ax}`;
  return `y = ${ax} ${b < 0 ? '−' : '+'} ${Math.abs(b)}`;
}

function figura(p: Params, a: number, b: number): ReturnType<typeof scene> {
  const base = afimScene(a, b, {
    span: SPAN,
    step: 1,
    unidade: UN,
    title: 'Plano cartesiano com dois pontos e uma reta ajustável',
    desc: '',
  });
  const y1 = p.a * p.x1 + p.b;
  const y2 = p.a * p.x2 + p.b;
  const pontos: Shape[] = [
    { t: 'point', p: { x: p.x1 * UN, y: y1 * UN }, role: 'given', name: 'A' },
    { t: 'point', p: { x: p.x2 * UN, y: y2 * UN }, role: 'given', name: 'B' },
  ];
  return scene([...base.shapes, ...pontos], {
    viewBox: base.viewBox,
    a11y: {
      title: 'Plano cartesiano com dois pontos e uma reta ajustável',
      desc:
        `Plano cartesiano de −${SPAN} a ${SPAN}, com os pontos A${pt(p.x1, y1)} e B${pt(p.x2, y2)} marcados. ` +
        `A reta desenhada agora é ${lei(a, b)}.`,
    },
  });
}

export const ajustarReta: ItemTemplate<Params, null> = {
  id: templateId('h1-08-K02-ajustar-reta'),
  // v2: malha quadriculada no lugar da malha de pontos.
  version: 2,
  taskModel: taskModelCode('H1.8.K02'),
  skills: [skillCode('H1.8')],
  axis: 'I',
  level: 'N3',
  title: 'Ajustar a reta até passar por dois pontos',
  responseModel: 'GPCM',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: () => null,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (!Number.isInteger(p.a)) fontes.push('non-round-numbers');
    // Sem ponto no eixo y, o b não se lê direto: é preciso prolongar a reta.
    if (p.x1 !== 0 && p.x2 !== 0) fontes.push('steps.3plus');
    return fontes;
  },
  build(p) {
    const y1 = p.a * p.x1 + p.b;
    const y2 = p.a * p.x2 + p.b;
    const A = `A${pt(p.x1, y1)}`;
    const B = `B${pt(p.x2, y2)}`;
    const naReta = (v: Readonly<Record<string, number>>, x: number, y: number): boolean =>
      igual((v['a'] ?? A0) * x + (v['b'] ?? B0), y);

    const alvo: ControlTarget = {
      describe: [txt(`A reta y = ax + b passando por ${A} e ${B}.`)],
      correto: (v) => igual(v['a'], p.a) && igual(v['b'], p.b),
      diagnostics: [
        { error: FUN_TROCA_A_B, test: (v) => igual(v['a'], p.b) && igual(v['b'], p.a) },
        { error: FUN_SINAL_ANGULAR, test: (v) => igual(v['a'], -p.a) && igual(v['b'], p.b) },
        { error: FUN_AJUSTE_PARALELA, test: (v) => igual(v['a'], p.a) },
        { error: FUN_AJUSTE_SO_CORTE, test: (v) => igual(v['b'], p.b) },
        { error: FUN_AJUSTE_UM_PONTO, test: (v) => naReta(v, p.x1, y1) !== naReta(v, p.x2, y2) },
      ],
      partialCredit: [
        {
          id: 'inclinacao',
          label: 'Inclinação certa',
          weight: 0.5,
          test: (v) => igual(v['a'], p.a),
        },
        {
          id: 'corte',
          label: 'Corte no eixo y certo',
          weight: 0.5,
          test: (v) => igual(v['b'], p.b),
        },
      ],
      analyze: (v) => {
        const a = v['a'] ?? A0;
        const b = v['b'] ?? B0;
        const em = (x: number): string => pt(x, a * x + b).replace(/(\d)\.5/g, '$1,5');
        if (igual(a, p.a) && igual(b, p.b))
          return [txt(`A reta ${lei(a, b)} passa por ${A} e por ${B}.`)];
        return [
          txt(
            `Com a = ${fracao(a)} e b = ${menos(b)}, a reta é ${lei(a, b)}: em x = ${menos(p.x1)}, ela passa por ${em(p.x1)}, ` +
              `e em x = ${menos(p.x2)}, por ${em(p.x2)}. Os pontos pedidos são ${A} e ${B}.`,
          ),
        ];
      },
    };

    const dx = p.x2 - p.x1;
    const dy = y2 - y1;
    return {
      stem: [
        txt(
          `No plano cartesiano abaixo, estão marcados os pontos ${A} e ${B}. A reta desenhada é o gráfico de `,
        ),
        tex('y = ax + b'),
        txt(', e os controles abaixo da figura mudam os valores de '),
        tex('a'),
        txt(' e de '),
        tex('b'),
        txt('.'),
      ],
      comando: [txt('Ajuste os controles até que a reta passe pelos pontos A e B.')],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'plano',
          alt:
            `Plano cartesiano de −${SPAN} a ${SPAN}, com os pontos ${A} e ${B} marcados e a reta y = ax + b, ` +
            'que muda com os controles a e b.',
          controls: [
            {
              id: 'a',
              label: [tex('a')],
              min: A_MIN,
              max: A_MAX,
              step: 0.5,
              initial: A0,
              format: fracao,
            },
            {
              id: 'b',
              label: [tex('b')],
              min: B_MIN,
              max: B_MAX,
              step: 1,
              initial: B0,
              format: menos,
            },
          ],
          frame: (v) => figura(p, v['a'] ?? A0, v['b'] ?? B0),
        },
      ],
      interaction: { kind: 'control-fit', stimulusId: 'plano', controles: ['a', 'b'], alvo },
      dicas: [
        [
          txt(
            'O controle b muda a altura em que a reta corta o eixo y; o controle a muda a inclinação.',
          ),
        ],
        [
          txt(
            `De A para B, x aumenta ${dx}. Quanto y varia? A inclinação é a variação de y para cada 1 de x.`,
          ),
        ],
        [
          txt(
            'Acerte primeiro a inclinação, deixando a reta paralela ao caminho de A até B; depois desloque-a com b até ela passar pelos dois pontos.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(
          `De A para B, x aumenta ${dx} e y ${dy >= 0 ? 'aumenta' : 'diminui'} ${Math.abs(dy)}. `,
        ),
        txt(
          `A inclinação é quanto y varia para cada 1 de x: a = ${menos(dy)}/${dx} = ${fracao(p.a)}.`,
        ),
        br,
        txt(
          `Com essa inclinação, a partir de A: b = ${menos(y1)} − (${fracao(p.a)}) · (${menos(p.x1)}) = ${menos(p.b)}, ` +
            `a altura em que a reta cruza o eixo y. A reta é ${lei(p.a, p.b)}.`,
        ),
      ],
      declared:
        !Number.isInteger(p.a) && p.x1 !== 0 && p.x2 !== 0
          ? 'dificil'
          : p.x1 === 0 || p.x2 === 0
            ? 'facil'
            : 'media',
    };
  },
};
